import React, { useMemo, useState, useRef, useId } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import {
    Home,
    ChevronRight,
    Tag,
    Layers,
    Package,
    Search,
    X,
    School
} from "lucide-react";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from "@/components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AnimatedContainer } from "@/components/shared/AnimatedContainer";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { useUser } from "@/contexts/UserContext";
import { clientsApi, ProductPricingBySegment } from "@/lib/api";
import { useCycleRequiredHandler } from "@/hooks/useCycleRequiredHandler";
import { CyclePickerDialog } from "@/components/shared/CyclePickerDialog";
import { formatCurrency } from "@/lib/formatters";
import { codeSeries } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { useKeyboardShortcuts, ShortcutConfig } from "@/hooks/useKeyboardShortcuts";

interface PricingSeriesTableProps {
    data: any[];
}

const PricingSeriesTable = ( { data }: PricingSeriesTableProps ) => {
    const columns = useMemo<ColumnDef<any>[]>( () => [
        {
            accessorKey: "productDescription",
            header: 'Produto',
            cell: ( { row } ) => (
                <div className="flex flex-col py-0.5" role="cell">
                    <span className="font-semibold text-foreground text-sm">
                        {row.original.productDescription || row.original.descricaoProduto || row.original.descricao_produto}
                    </span>
                    <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-wider">
                        SKU: {row.original.productSku || row.original.sku}
                    </span>
                </div>
            )
        },
        {
            accessorKey: "materialUseName",
            header: "Utilização",
            cell: ( { row } ) => {
                const use = ( row.original.materialUseName || "" ).toLowerCase();
                const isAluno = use === "aluno";
                return (
                    <Badge variant="outline" className={cn(
                        "text-[10px] font-bold uppercase px-2 py-0 border-opacity-30",
                        isAluno ? "bg-blue-50 text-blue-800 border-blue-600" : "bg-purple-50 text-purple-800 border-purple-600"
                    )} aria-label={`Perfil de utilização: ${ row.original.materialUseName }`}>
                        {row.original.materialUseName}
                    </Badge>
                );
            }
        },
        {
            accessorKey: "contractualQuantity",
            header: "Qtd. Contratual",
            cell: ( { row } ) => (
                <div className="text-left font-medium text-slate-600" aria-label={`Quantidade contratual: ${ row.original.contractualQuantity || 0 }`}>
                    {row.original.contractualQuantity || 0}
                </div>
            )
        },
        {
            accessorKey: "baseNetPrice",
            header: "Valor unitário",
            cell: ( { row } ) => (
                <div className="text-left font-medium text-slate-600" aria-label={`Preço base: ${ formatCurrency( row.original.baseNetPrice || 0 ) }`}>
                    {formatCurrency( row.original.baseNetPrice || row.original.priceWithoutDiscount || 0 )}
                </div>
            )
        },
        {
            accessorKey: "adjustment",
            header: "Reajuste (%)",
            cell: ( { row } ) => (
                <div className="text-left">
                    <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-100" aria-label={`Percentual de reajuste: ${ row.original.adjustment || 0 }%`}>
                        {row.original.adjustment || 0}%
                    </span>
                </div>
            )
        },
        {
            accessorKey: "adjustedPrice",
            header: "Preço Reajustado",
            cell: ( { row } ) => (
                <div className="text-left font-bold text-slate-900" aria-label={`Preço final reajustado: ${ formatCurrency( row.original.adjustedPrice || 0 ) }`}>
                    {formatCurrency( row.original.adjustedPrice || row.original.productPrice || row.original.preco || 0 )}
                </div>
            )
        }
    ], [] );

    const table = useReactTable( {
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
    } );

    return (
        <div className="overflow-hidden border rounded-xl bg-white shadow-sm mt-4 mx-4 mb-4">
            <Table aria-label="Produtos e preços por série">
                <TableHeader className="bg-slate-50 border-b">
                    {table.getHeaderGroups().map( ( headerGroup ) => (
                        <TableRow key={headerGroup.id} className="hover:bg-transparent border-none">
                            {headerGroup.headers.map( ( header ) => (
                                <TableHead key={header.id} scope="col" className="font-bold text-slate-700 h-10 px-4">
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                </TableHead>
                            ) )}
                        </TableRow>
                    ) )}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map( ( row ) => (
                            <TableRow key={row.id} className="hover:bg-slate-50 border-b last:border-0 transition-colors focus-within:bg-slate-50 outline-none">
                                {row.getVisibleCells().map( ( cell ) => (
                                    <TableCell key={cell.id} className="py-3 px-4">
                                        {flexRender( cell.column.columnDef.cell, cell.getContext() )}
                                    </TableCell>
                                ) )}
                            </TableRow>
                        ) )
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center text-slate-500 italic">
                                Nenhum produto encontrado.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

const SEGMENT_ORDER = [
    "Educação Infantil",
    "Fundamental Anos Iniciais",
    "Fundamental Anos Finais",
    "Ensino Médio",
    "Pré Vestibular"
];

const ProductPricing = () => {
    const { selectedClient } = useUser();
    const {
        selectedCycleYear,
        availableCycles,
        dialogOpen: cycleDialogOpen,
        setDialogOpen: setCycleDialogOpen,
        handleCycleError,
        selectCycle,
    } = useCycleRequiredHandler();
    const [ searchTerm, setSearchTerm ] = useState( "" );
    const [ utilizationFilter, setUtilizationFilter ] = useState( "todos" );

    const searchInputRef = useRef<HTMLInputElement>( null );
    const searchInputId = useId();

    useKeyboardShortcuts( [
        {
            key: "f",
            ctrlKey: true,
            action: ( e ) => {
                e?.preventDefault();
                searchInputRef.current?.focus();
            }
        }
    ] as ShortcutConfig[] );

    const { data: pricingData, isLoading } = useQuery( {
        queryKey: [ 'product-pricing', selectedClient?.clientId, selectedCycleYear ],
        queryFn: async () => {
            if ( !selectedClient?.clientId ) return [];
            try {
                const response = await clientsApi.getProductPricing( selectedClient.clientId, selectedCycleYear ?? undefined );
                return response.data;
            } catch ( error ) {
                if ( handleCycleError( error ) ) return [];
                throw error;
            }
        },
        enabled: !!selectedClient?.clientId,
    } );

    const filteredData = useMemo( () => {
        if ( !pricingData ) return [];

        const lowerSearch = searchTerm.toLowerCase();

        return pricingData
            .map( ( segment: ProductPricingBySegment ) => {
                const filteredSeries = segment.series.map( series => {
                    const filteredProducts = series.products.filter( p => {
                        const matchesSearch = !searchTerm.trim() ||
                            ( p.productDescription || "" ).toLowerCase().includes( lowerSearch ) ||
                            ( p.productSku || "" ).toLowerCase().includes( lowerSearch );

                        const matchesUtilization = utilizationFilter === "todos" ||
                            ( p.materialUseName || "" ).toLowerCase() === utilizationFilter;

                        return matchesSearch && matchesUtilization && p.isActive !== false;
                    } );
                    return { ...series, products: filteredProducts };
                } ).filter( series => series.products.length > 0 );

                return { ...segment, series: filteredSeries };
            } )
            .filter( ( segment: ProductPricingBySegment ) => segment.series.length > 0 )
            .sort( ( a, b ) => {
                const indexA = SEGMENT_ORDER.indexOf( a.segmentName );
                const indexB = SEGMENT_ORDER.indexOf( b.segmentName );

                if ( indexA !== -1 && indexB !== -1 ) return indexA - indexB;
                if ( indexA !== -1 ) return -1;
                if ( indexB !== -1 ) return 1;

                return ( a.segmentName || "" ).localeCompare( b.segmentName || "" );
            } );
    }, [ pricingData, searchTerm, utilizationFilter ] );

    const stats = useMemo( () => {
        const allProducts = filteredData.flatMap( s => s.series.flatMap( sr => sr.products ) );
        return {
            totalProducts: allProducts.length,
            totalSeries: filteredData.reduce( ( acc, s ) => acc + s.series.length, 0 ),
            segments: filteredData.length
        };
    }, [ filteredData ] );

    if ( isLoading ) {
        return (
            <div className="min-h-screen bg-background" role="status" aria-label="Carregando tabela de preços">
                <header className="container mx-auto px-4 pt-6 space-y-6">
                    <Skeleton className="h-4 w-48" />
                    <div className="flex justify-between items-center">
                        <Skeleton className="h-10 w-64" />
                        <Skeleton className="h-12 w-48 rounded-lg" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[ 1, 2, 3 ].map( i => <Skeleton key={i} className="h-24 w-full rounded-xl" /> )}
                    </div>
                </header>
                <main className="container mx-auto px-4 py-8 space-y-4">
                    {[ 1, 2, 3 ].map( i => <Skeleton key={i} className="h-16 w-full rounded-xl" /> )}
                </main>
            </div>
        );
    }

    return (
        <AnimatedContainer className="min-h-screen bg-background pb-12">
            <CyclePickerDialog
                open={cycleDialogOpen}
                availableCycles={availableCycles}
                onOpenChange={setCycleDialogOpen}
                onSelect={selectCycle}
            />
            <header className="container mx-auto px-4 pt-6 space-y-6" role="banner">
                <nav aria-label="Navegação Breadcrumb">
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link to="/customer-portal" className="flex items-center gap-1 transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-primary rounded px-1">
                                        <Home className="h-3.5 w-3.5" aria-hidden="true" />
                                        <span>Início</span>
                                    </Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator aria-hidden="true" />
                            <BreadcrumbItem>
                                <BreadcrumbPage className="flex items-center gap-1 font-semibold text-slate-900">
                                    <Tag className="h-3.5 w-3.5" aria-hidden="true" />
                                    <span>Tabela de Preços</span>
                                </BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>
                </nav>

                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b pb-6 border-slate-100">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" id="main-heading">
                            Tabela de Preços
                        </h1>
                        {/* <p className="text-slate-600 mt-1 text-base">
                            Consulte os valores contratuais da unidade <span className="font-bold text-blue-700">{selectedClient?.clientFantasyName || selectedClient?.nome_cliente || selectedClient?.nome_fantasia}</span>.
                        </p> */}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" aria-label="Estatísticas gerais">
                    {[
                        { label: "Total de Produtos", value: stats.totalProducts, icon: Package, color: "text-blue-700", bg: "bg-blue-100/50" },
                        { label: "Séries Atendidas", value: stats.totalSeries, icon: Layers, color: "text-indigo-700", bg: "bg-indigo-100/50" },
                        { label: "Segmentos", value: stats.segments, icon: Tag, color: "text-emerald-700", bg: "bg-emerald-100/50" }
                    ].map( ( item, i ) => (
                        <Card key={i} className="border border-slate-200 shadow-sm bg-white hover:border-slate-300 transition-colors">
                            <CardContent className="pt-5 pb-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-1">{item.label}</p>
                                        <p className="text-3xl font-black text-slate-900 tracking-tighter">{item.value}</p>
                                    </div>
                                    <div className={cn( "p-2.5 rounded-xl", item.bg )}>
                                        <item.icon className={cn( "h-6 w-6", item.color )} aria-hidden="true" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ) )}
                </div>
            </header>

            <main className="container mx-auto px-4 py-8 space-y-6" aria-labelledby="main-heading">
                <section className="flex flex-col md:flex-row justify-between items-center gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200" aria-label="Filtros e Busca">
                    <div className="flex flex-col gap-2 w-full md:w-auto">
                        <span id="label-filter-use" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Filtrar por Utilização</span>
                        <Tabs value={utilizationFilter} onValueChange={setUtilizationFilter} className="w-fit" aria-labelledby="label-filter-use">
                            <TabsList className="bg-slate-200/50 h-10 p-1 border border-slate-200">
                                <TabsTrigger value="todos" className="px-6 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm transition-all focus-visible:ring-2 focus-visible:ring-primary rounded-md">Todos</TabsTrigger>
                                <TabsTrigger value="aluno" className="px-6 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm transition-all focus-visible:ring-2 focus-visible:ring-primary rounded-md">Aluno</TabsTrigger>
                                <TabsTrigger value="professor" className="px-6 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-blue-700 data-[state=active]:shadow-sm transition-all focus-visible:ring-2 focus-visible:ring-primary rounded-md">Professor</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>

                    <div className="flex flex-col gap-2 w-full md:w-[400px]">
                        <label htmlFor={searchInputId} className="text-xs font-bold text-slate-500 uppercase tracking-widest">Busca Rápida</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden="true" />
                            <Input
                                id={searchInputId}
                                ref={searchInputRef}
                                placeholder="Descrição ou SKU... (Ctrl+F)"
                                value={searchTerm}
                                onChange={( e ) => setSearchTerm( e.target.value )}
                                className="pl-10 h-11 bg-white border-slate-200 shadow-sm focus-visible:ring-blue-600 focus-visible:border-blue-600 font-medium"
                                aria-label="Buscar produtos na tabela de preços"
                            />
                            {searchTerm && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                    onClick={() => setSearchTerm( "" )}
                                    aria-label="Limpar busca"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </div>
                </section>

                <div className="sr-only" aria-live="polite" role="status">
                    {searchTerm ? `${ stats.totalProducts } produtos encontrados para "${ searchTerm }"` : "Mostrando todos os produtos"}
                </div>

                <section className="space-y-4" aria-label="Listagem de Preços">
                    {!filteredData || filteredData.length === 0 ? (
                        <EmptyState
                            title="Nenhum dado encontrado"
                            message="Não foram encontrados produtos para os critérios selecionados."
                            className="py-24 bg-white rounded-2xl border-2 border-dashed border-slate-200 shadow-none"
                        />
                    ) : (
                        <Accordion type="single" collapsible className="w-full space-y-4">
                            {filteredData.map( ( segment: ProductPricingBySegment, sIdx: number ) => (
                                <AccordionItem
                                    key={segment.segmentName || `segment-${ sIdx }`}
                                    value={`segment-${ sIdx }`}
                                    className="border rounded-2xl overflow-hidden bg-white shadow-sm border-slate-200 transition-all hover:border-slate-300"
                                >
                                    <AccordionTrigger className="hover:no-underline py-5 px-6 hover:bg-slate-50 transition-all group focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary outline-none text-left">
                                        <div className="flex items-center gap-5 text-left">
                                            <div className="bg-slate-100 p-2.5 rounded-xl text-slate-600 group-hover:bg-blue-600 group-hover:text-white transition-all transform group-data-[state=open]:scale-110">
                                                <Layers className="h-6 w-6" aria-hidden="true" />
                                            </div>
                                            <div className="flex flex-col items-start text-left">
                                                <h2 className="text-xl font-extrabold text-slate-900 leading-tight">
                                                    {segment.segmentName || "Segmento não definido"}
                                                </h2>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                                                        {segment.series.length} {segment.series.length === 1 ? 'Série' : 'Séries'}
                                                    </span>
                                                    <span className="text-slate-300" aria-hidden="true">•</span>
                                                    <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                                                        {segment.series.reduce( ( acc, ser ) => acc + ser.products.length, 0 )} Produtos
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="p-0 border-t border-slate-100 bg-slate-50/30">
                                        <div className="p-4 sm:p-6">
                                            <Accordion type="single" collapsible className="w-full space-y-4">
                                                {[ ...segment.series ]
                                                    .sort( ( a, b ) => {
                                                        const orderA = codeSeries( a.seriesName ) ?? 999;
                                                        const orderB = codeSeries( b.seriesName ) ?? 999;
                                                        return orderA - orderB;
                                                    } )
                                                    .map( ( seriesGroup, pIdx ) => (
                                                        <AccordionItem
                                                            key={seriesGroup.seriesName || `series-${ pIdx }`}
                                                            value={`series-${ pIdx }`}
                                                            className="border rounded-xl bg-white shadow-sm overflow-hidden border-slate-200 transition-all hover:border-blue-200"
                                                        >
                                                            <AccordionTrigger className="py-4 px-5 hover:no-underline hover:bg-slate-50 transition-all border-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-600 outline-none">
                                                                <div className="flex items-center justify-between w-full pr-4 text-left">
                                                                    <div className="flex items-center gap-4 text-left">
                                                                        <div className="p-2 rounded-lg bg-slate-50 text-slate-400 group-data-[state=open]:bg-blue-50 group-data-[state=open]:text-blue-600 transition-colors">
                                                                            <Package className="h-5 w-5" aria-hidden="true" />
                                                                        </div>
                                                                        <h3 className="text-base font-bold text-slate-800">
                                                                            {seriesGroup.seriesName || "Série não definida"}
                                                                        </h3>
                                                                        <Badge variant="secondary" className="text-[10px] font-bold bg-slate-100 text-slate-600 border-none px-2 h-5">
                                                                            {seriesGroup.products.length} {seriesGroup.products.length === 1 ? 'Item' : 'Itens'}
                                                                        </Badge>
                                                                    </div>
                                                                </div>
                                                            </AccordionTrigger>
                                                            <AccordionContent className="p-0 border-t border-slate-100">
                                                                <PricingSeriesTable data={seriesGroup.products} />
                                                            </AccordionContent>
                                                        </AccordionItem>
                                                    ) )}
                                            </Accordion>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ) )}
                        </Accordion>
                    )}
                </section>
            </main>
        </AnimatedContainer>
    );
};

export default ProductPricing;
